/**
 * Modales "Asignar" y "Liberar" de un recurso — Sprint 22.
 *
 * Reemplazan al select "Residente actual" que vivía dentro del formulario de
 * edición de `/tesoreria/recursos`. Ese select mutaba `residente_actual_id`
 * por PATCH sin validar nada (permitía asignar a un residente de otra casa, o
 * acumular llaveros en una misma persona) y sin dejar registro de por qué
 * había cambiado el portador.
 *
 * Ahora son dos operaciones de flujo:
 *   Asignar → POST /recursos/{id}/asignar   (valida los invariantes)
 *   Liberar → POST /recursos/{id}/liberar   (exige motivo, cierra historial)
 *
 * Doctrina: un recurso asignado no se desasigna a voluntad. El tesorero solo
 * dispone de los tres motivos que corresponden a una decisión suya; el resto
 * (baja de residente, cierre de tenencia, baja de vehículo, estado terminal)
 * los dispara el sistema solo.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { listarRecursosDeLote, listarVehiculosDeLote } from '../../lib/api/lotes';
import {
  asignar as asignarRecurso,
  liberar as liberarRecurso,
  type MotivoLiberacion,
} from '../../lib/api/recursos';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';
import type {
  RecursoConSuspensionRead,
  RecursoRead,
  VehiculoRead,
} from '../../lib/api/types';
import {
  residentesConLlaveroVivo,
  vehiculosConCrVivo,
} from '../../lib/recursos';
import { Button } from '../ui/Button';

const ESTILO_SELECT =
  'w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary';

/** Motivos que el tesorero puede elegir a mano, con su explicación. Los demás
 *  motivos de `MOTIVOS_LIBERACION` los escribe el sistema. */
const MOTIVOS: { value: MotivoLiberacion; label: string; ayuda: string }[] = [
  {
    value: 'reemplazo',
    label: 'Reemplazo',
    ayuda:
      'El recurso se reemplaza por otro (pérdida, robo, deterioro o falla).',
  },
  {
    value: 'correccion_carga_inicial',
    label: 'Corrección de la carga inicial',
    ayuda:
      'El dato venía mal desde la carga del go-live, no refleja quién lo porta.',
  },
  {
    value: 'correccion_tesorero',
    label: 'Corrección de un dato mal ingresado',
    ayuda: 'Se registró a la persona equivocada. Explica qué pasó.',
  },
];

function extraerError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : `Error ${err.status}`;
  }
  return fallback;
}

function Marco({
  titulo,
  onCerrar,
  onSubmit,
  children,
  error,
  accion,
  puedeEnviar,
  enviando,
}: {
  titulo: string;
  onCerrar: () => void;
  onSubmit: (e: FormEvent) => void;
  children: React.ReactNode;
  error: string | null;
  accion: string;
  puedeEnviar: boolean;
  enviando: boolean;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      <form
        onSubmit={onSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">{titulo}</h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        {children}
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={!puedeEnviar || enviando}>
            {enviando ? 'Guardando…' : accion}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ModalAsignarRecurso({
  recurso,
  onListarResidentes,
  onCerrar,
  onAsignado,
}: {
  recurso: RecursoRead;
  onListarResidentes: (loteId: string) => Promise<ResidenteConPersonaRead[]>;
  onCerrar: () => void;
  onAsignado: () => void;
}) {
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [recursos, setRecursos] = useState<RecursoConSuspensionRead[]>([]);
  const [vehiculos, setVehiculos] = useState<VehiculoRead[]>([]);
  const [residenteId, setResidenteId] = useState('');
  const [vehiculoId, setVehiculoId] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esCR = recurso.tipo === 'control_remoto';

  useEffect(() => {
    let cancelado = false;
    Promise.all([
      onListarResidentes(recurso.lote_id),
      listarRecursosDeLote(recurso.lote_id),
      esCR ? listarVehiculosDeLote(recurso.lote_id) : Promise.resolve([]),
    ])
      .then(([rs, recs, vs]) => {
        if (cancelado) return;
        setResidentes(rs.filter((r) => r.estado === 'aprobado'));
        setRecursos(recs);
        setVehiculos(vs.filter((v) => v.activo));
      })
      .catch(() => {
        if (cancelado) return;
        setError('No se pudo cargar la información de la casa.');
      });
    return () => {
      cancelado = true;
    };
  }, [recurso.lote_id, onListarResidentes, esCR]);

  // Candidatos ya descontados: el backend rechazaría con 409 igual, pero el
  // tesorero no tiene por qué descubrirlo apretando el botón.
  const conLlavero = residentesConLlaveroVivo(recursos);
  const conControl = vehiculosConCrVivo(recursos);
  const candidatos =
    recurso.tipo === 'llavero'
      ? residentes.filter((r) => !conLlavero.has(r.id))
      : residentes;
  const autos = vehiculos.filter((v) => !conControl.has(v.id));

  const puedeEnviar = residenteId !== '' && (!esCR || vehiculoId !== '');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!puedeEnviar || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await asignarRecurso(recurso.id, residenteId, esCR ? vehiculoId : null);
      onAsignado();
    } catch (err) {
      setError(extraerError(err, 'No se pudo asignar.'));
      setEnviando(false);
    }
  }

  return (
    <Marco
      titulo={`Asignar ${recurso.identificador}`}
      onCerrar={onCerrar}
      onSubmit={handleSubmit}
      error={error}
      accion="Asignar"
      puedeEnviar={puedeEnviar}
      enviando={enviando}
    >
      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">Persona</span>
        <select
          value={residenteId}
          onChange={(e) => setResidenteId(e.target.value)}
          aria-label="Persona"
          className={ESTILO_SELECT}
          required
        >
          <option value="">— elige —</option>
          {candidatos.map((r) => (
            <option key={r.id} value={r.id}>
              {r.persona_nombre_completo}
              {r.parentesco !== 'titular' && ` (${r.parentesco})`}
            </option>
          ))}
        </select>
        {residentes.length > 0 && candidatos.length === 0 && (
          <span className="text-[11px] text-state-warning">
            Todas las personas registradas en esta casa ya tienen llavero (cada
            persona lleva uno). Registra a quien falte, o libera el llavero
            anterior si es un reemplazo.
          </span>
        )}
      </label>

      {esCR && (
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Vehículo</span>
          <select
            value={vehiculoId}
            onChange={(e) => setVehiculoId(e.target.value)}
            aria-label="Vehículo"
            className={ESTILO_SELECT}
            required
          >
            <option value="">— elige —</option>
            {autos.map((v) => (
              <option key={v.id} value={v.id}>
                {v.patente}
                {v.marca && ` · ${v.marca}`}
              </option>
            ))}
          </select>
          {vehiculos.length > 0 && autos.length === 0 && (
            <span className="text-[11px] text-state-warning">
              Todos los vehículos de esta casa ya tienen control remoto.
            </span>
          )}
        </label>
      )}
    </Marco>
  );
}

export function ModalLiberarRecurso({
  recurso,
  onCerrar,
  onLiberado,
}: {
  recurso: RecursoRead;
  onCerrar: () => void;
  onLiberado: () => void;
}) {
  const [motivo, setMotivo] = useState<MotivoLiberacion>('reemplazo');
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const motivoActual = MOTIVOS.find((m) => m.value === motivo);
  const notasObligatorias = motivo === 'correccion_tesorero';
  const puedeEnviar = !notasObligatorias || notas.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!puedeEnviar || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      await liberarRecurso(recurso.id, {
        motivo,
        notas: notas.trim() || null,
      });
      onLiberado();
    } catch (err) {
      setError(extraerError(err, 'No se pudo liberar.'));
      setEnviando(false);
    }
  }

  return (
    <Marco
      titulo={`Liberar ${recurso.identificador}`}
      onCerrar={onCerrar}
      onSubmit={handleSubmit}
      error={error}
      accion="Liberar"
      puedeEnviar={puedeEnviar}
      enviando={enviando}
    >
      <p className="text-sm text-text-secondary">
        El recurso vuelve a la bolsa de la casa para que se lo asignen a otra
        persona. No cambia su estado: sigue existiendo físicamente.
      </p>

      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">Motivo</span>
        <select
          value={motivo}
          onChange={(e) => setMotivo(e.target.value as MotivoLiberacion)}
          aria-label="Motivo"
          className={ESTILO_SELECT}
          required
        >
          {MOTIVOS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        {motivoActual && (
          <span className="text-[11px] text-text-muted">{motivoActual.ayuda}</span>
        )}
      </label>

      <label className="text-sm space-y-1 block">
        <span className="text-text-secondary">
          Detalle{' '}
          {notasObligatorias ? (
            <span className="text-state-danger">*</span>
          ) : (
            <span className="text-text-muted">(opcional)</span>
          )}
        </span>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={3}
          aria-label="Detalle"
          className={ESTILO_SELECT}
        />
      </label>
    </Marco>
  );
}
